import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { toApiError, useApi, type InvoiceItemPresetResponse } from '@/shared/api';

const queryKey = ['invoiceItemPresets', 'all'];

/**
 * Der Katalog samt seiner Mutationen. Die Einstellungs-Ansicht zeigt bewusst auch stillgelegte
 * Positionen — nur der Builder bekommt später die gefilterte Auswahl.
 */
export function useInvoiceItemPresets() {
  const api = useApi();
  const qc = useQueryClient();
  // Während des Ziehens gilt die lokale Reihenfolge, sonst die des Servers.
  const [localOrder, setLocalOrder] = useState<InvoiceItemPresetResponse[] | null>(null);

  const query = useQuery({
    queryKey,
    queryFn: () => api.invoiceItemPresets.list(false),
  });

  const showError = async (err: unknown) => {
    const error = await toApiError(err);
    toast.error(error.detail ?? error.title);
  };

  // Bewusst eine eigene Lambda statt der Methode direkt: TanStack reicht der `mutationFn` einen
  // zweiten Kontext-Parameter, der sonst als zusätzliches Argument beim Client landet.
  const create = useMutation({
    mutationFn: (body: Parameters<typeof api.invoiceItemPresets.create>[0]) =>
      api.invoiceItemPresets.create(body),
    onSuccess: () => {
      toast.success('Position angelegt');
      void qc.invalidateQueries({ queryKey });
    },
    onError: showError,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof api.invoiceItemPresets.update>[1];
    }) => api.invoiceItemPresets.update(id, body),
    onSuccess: () => {
      toast.success('Position gespeichert');
      void qc.invalidateQueries({ queryKey });
    },
    onError: showError,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.invoiceItemPresets.delete(id),
    onSuccess: () => {
      toast.success('Position entfernt');
      void qc.invalidateQueries({ queryKey });
    },
    onError: showError,
  });

  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) => api.invoiceItemPresets.reorder({ orderedIds }),
    onSuccess: (server) => {
      setLocalOrder(null);
      qc.setQueryData(queryKey, server);
    },
    onError: async (err) => {
      // Die optimistische Reihenfolge zurücknehmen, sonst zeigt die Liste eine Ordnung,
      // die auf dem Server nie ankam.
      setLocalOrder(null);
      await showError(err);
      void qc.invalidateQueries({ queryKey });
    },
  });

  return {
    presets: localOrder ?? query.data ?? [],
    isLoading: query.isLoading,
    hasError: query.isError,
    saving: create.isPending || update.isPending,
    removing: remove.isPending,
    create,
    update,
    remove,
    applyOrder: (next: InvoiceItemPresetResponse[]) => {
      setLocalOrder(next);
      reorder.mutate(next.map((preset) => preset.id));
    },
  };
}
